import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchLoungePermissionSettings, updateLoungeLevelPermission,
  searchAdminPermissionAccounts, fetchLoungeAccountPermission, updateLoungeAccountPermission,
} from '../api/adminPermissionApi';

const EMPTY = { view: true, create: false, edit: false, publish: false, delete: false, manage_all: false };
const PERMISSIONS = [
  { key: 'view', title: '라운지 메뉴 / 게시글 열람', desc: '라운지 페이지, 본인 글 목록 및 상세 보기' },
  { key: 'create', title: '새 글 작성 / 이미지 업로드', desc: '초안 작성 및 편집에 사용할 이미지 업로드' },
  { key: 'edit', title: '게시글 수정', desc: '본인 글 수정 (게시 중인 글에는 게시 권한도 요구)' },
  { key: 'publish', title: '게시 · 결제 · 게시 중단', desc: '게시비용 차감, 재게시, 게시 중지, 게시 중 글 변경' },
  { key: 'delete', title: '게시글 삭제', desc: '본인 글 삭제' },
  { key: 'manage_all', title: '다른 관리자의 글 관리', desc: '다른 계정의 글에도 허용된 작업을 적용. 단독으로 작성·수정·삭제를 허용하지 않습니다.' },
];
const LEVELS = [1, 2, 3, 4];
const card = 'bg-white border border-gray-200 rounded-2xl shadow-sm p-5 sm:p-6';
const action = 'px-4 py-2.5 rounded-lg bg-gray-900 text-white font-semibold text-sm disabled:opacity-40 hover:bg-gray-800';
function Choice({ value, onChange, options, disabled }) {
  return <select className="min-w-28 border border-gray-200 rounded-lg bg-white px-3 py-2 text-sm" value={String(value)} disabled={disabled} onChange={(e) => {
    const v = e.target.value;
    onChange(v === 'inherit' ? null : v === 'true');
  }}>
    {options.map((opt) => <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>)}
  </select>;
}
const LEVEL_OPTIONS = [{ value: true, label: '허용' }, { value: false, label: '차단' }];
const ACCOUNT_OPTIONS = [{ value: 'inherit', label: '레벨 기본값' }, ...LEVEL_OPTIONS];
function Feedback({ message, error }) {
  return message ? <p role="status" className={`mt-3 text-sm ${error ? 'text-red-600' : 'text-emerald-700'}`}>{message}</p> : null;
}
export default function LoungePermissionSettings() {
  const [tab, setTab] = useState('level');
  const [levels, setLevels] = useState({});
  const [originalLevels, setOriginalLevels] = useState({});
  const [selectedLevel, setSelectedLevel] = useState(2);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [searchItems, setSearchItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [initialOverrides, setInitialOverrides] = useState({});
  const [levelForAccount, setLevelForAccount] = useState(EMPTY);
  const [reason, setReason] = useState('');

  const feedback = (msg, isError = false) => { setMessage(msg); setError(isError); };
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLoungePermissionSettings();
      setLevels(data.levels || {});
      setOriginalLevels(data.levels || {});
      feedback('');
    } catch (err) { feedback(err.message, true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const current = levels[`lv${selectedLevel}`] || EMPTY;
  const levelDirty = JSON.stringify(current) !== JSON.stringify(originalLevels[`lv${selectedLevel}`] || EMPTY);
  const accountDirty = JSON.stringify(overrides) !== JSON.stringify(initialOverrides);
  const effective = Object.fromEntries(PERMISSIONS.map(({ key }) => [key, overrides[key] == null ? levelForAccount[key] : overrides[key]]));

  const saveLevel = async () => {
    if (!window.confirm(`LV${selectedLevel}의 기본 권한을 저장할까요? 해당 레벨 관리자에게 즉시 적용됩니다.`)) return;
    setBusy(true); feedback('');
    try {
      const data = await updateLoungeLevelPermission(selectedLevel, current, reason);
      const next = { ...levels, [`lv${selectedLevel}`]: data.permissions };
      setLevels(next); setOriginalLevels(next); setReason('');
      feedback(`LV${selectedLevel} 기본 권한이 저장되었습니다.`);
      if (selectedAccount?.level === selectedLevel) setLevelForAccount(data.permissions);
    } catch (err) { feedback(err.message, true); }
    finally { setBusy(false); }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (search.trim().length < 2) { feedback('아이디 또는 닉네임을 2글자 이상 입력해 주세요.', true); return; }
    setSearching(true); setSearchItems([]); setSelectedAccount(null); feedback('');
    try { const data = await searchAdminPermissionAccounts(search.trim()); setSearchItems(data.items || []); }
    catch (err) { feedback(err.message, true); }
    finally { setSearching(false); }
  };
  const selectAccount = async (account) => {
    setBusy(true); feedback('');
    try {
      const data = await fetchLoungeAccountPermission(account.userCode);
      setSelectedAccount(data.account);
      setOverrides(data.overrides); setInitialOverrides(data.overrides);
      setLevelForAccount(data.levelPolicy);
    } catch (err) { feedback(err.message, true); }
    finally { setBusy(false); }
  };
  const saveAccount = async () => {
    if (!selectedAccount) return;
    if (!window.confirm(`${selectedAccount.id} (${selectedAccount.nickname})의 개별 권한을 저장할까요?`)) return;
    setBusy(true); feedback('');
    try {
      const data = await updateLoungeAccountPermission(selectedAccount.userCode, overrides, reason);
      setOverrides(data.overrides); setInitialOverrides(data.overrides);
      setLevelForAccount(data.levelPolicy); setReason('');
      feedback('계정별 예외 권한이 저장되었습니다. 변경 내용은 다음 API 요청부터 적용됩니다.');
    } catch (err) { feedback(err.message, true); }
    finally { setBusy(false); }
  };
  return <div className="max-w-5xl mx-auto space-y-6 pb-16">
    <header>
      <div className="flex gap-2 items-center"><span className="text-xl">🔒</span><h1 className="text-xl sm:text-2xl font-bold text-gray-900">관리자 권한 설정</h1><span className="text-xs rounded-md bg-black text-white px-2 py-1">최고관리자 전용</span></div>
      <p className="mt-2 text-sm text-gray-500">이 화면에서는 라운지 관리 권한만 설정합니다. 운영 대시보드는 LV1~LV4에 기본 제공되며 본인 조직 범위로 제한됩니다.</p>
    </header>
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      <strong>기본 보안 정책</strong> · 최상위 LV1 계정은 모든 조회 권한을 유지합니다. LV2~LV4 운영 대시보드는 별도 허용·차단 설정 없이 본인과 하위 조직 통계만 조회할 수 있습니다. 이 설정은 라운지 관리 권한에만 적용되며, 계정별 설정이 레벨 기본값에 우선합니다.
    </div>
    <div className="flex border-b border-gray-200 gap-2">
      {[['level', '관리자 레벨별 권한'], ['account', '특정 관리자 예외 권한']].map(([key, label]) => <button key={key} onClick={() => { setTab(key); feedback(''); }} type="button" className={`px-4 py-3 font-semibold text-sm border-b-2 ${tab === key ? 'border-pink-600 text-gray-900' : 'border-transparent text-gray-500'}`}>{label}</button>)}
    </div>
    {tab === 'level' ? <section className={card}>
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center mb-5">
        <div><h2 className="font-bold text-gray-900">레벨별 기본 설정</h2><p className="text-xs text-gray-500 mt-1">모든 동일 레벨 계정에 적용됩니다. 개별 예외가 있으면 그 설정이 우선합니다.</p></div>
        <div className="flex gap-2">{LEVELS.map((level) => <button key={level} className={`px-3 py-2 rounded-lg border text-sm ${selectedLevel === level ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`} onClick={() => { setSelectedLevel(level); feedback(''); }}>LV{level}</button>)}</div>
      </div>
      <div className="divide-y divide-gray-100">{PERMISSIONS.map((permission) => <div key={permission.key} className="flex items-center justify-between gap-3 py-4">
        <div><p className="text-sm font-semibold text-gray-800">{permission.title}</p><p className="text-xs text-gray-500 mt-1">{permission.desc}</p></div>
        <Choice value={current[permission.key]} onChange={(v) => setLevels((prev) => ({ ...prev, [`lv${selectedLevel}`]: { ...current, [permission.key]: v } }))} options={LEVEL_OPTIONS} disabled={loading || busy}/>
      </div>)}</div>
      <div className="mt-5 flex items-center justify-between gap-4 flex-wrap"><span className="text-xs text-gray-500">LV1 최상위 루트 계정은 이 설정의 영향을 받지 않습니다.</span><button type="button" className={action} onClick={saveLevel} disabled={!levelDirty || loading || busy}>LV{selectedLevel} 기본 권한 저장</button></div>
    </section> : <section className={card}>
      <h2 className="font-bold text-gray-900">관리자 검색 및 선택</h2>
      <p className="text-xs text-gray-500 mt-1 mb-4">계정 아이디 또는 닉네임으로 검색합니다. 검색 결과를 선택한 다음 개별 권한을 지정하세요.</p>
      <form className="flex gap-2" onSubmit={runSearch}><input className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-sm" maxLength={60} placeholder="관리자 아이디 또는 닉네임 검색" value={search} onChange={(e) => setSearch(e.target.value)}/><button className={action} type="submit" disabled={searching || busy}>{searching ? '검색 중...' : '검색'}</button></form>
      {searchItems.length > 0 && <div className="mt-3 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">{searchItems.map((item) => <button key={item.userCode} type="button" onClick={() => selectAccount(item)} disabled={busy} className={`w-full px-4 py-3 text-left flex flex-wrap items-center justify-between hover:bg-gray-50 text-sm ${selectedAccount?.userCode === item.userCode ? 'bg-pink-50' : ''}`}><span><b>{item.id}</b> · {item.nickname} <span className="text-gray-400">#{item.userCode}</span></span><span className="text-xs text-gray-500">LV{item.level} · {item.active ? '활성' : '비활성 / 차단'}</span></button>)}</div>}
      {!searching && search.trim().length >= 2 && searchItems.length === 0 && !selectedAccount && <p className="mt-3 text-xs text-gray-400">검색 후 해당 관리자가 표시됩니다.</p>}
      {selectedAccount && <div className="mt-6 border-t pt-5 border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4"><div><h3 className="font-bold text-gray-900">{selectedAccount.id} · {selectedAccount.nickname}</h3><p className="text-xs text-gray-500">LV{selectedAccount.level} / 코드 {selectedAccount.userCode}</p></div><span className="rounded-md bg-gray-100 px-3 py-1.5 text-xs">예외값 없음 = 레벨 기본값 적용</span></div>
        <div className="divide-y divide-gray-100">{PERMISSIONS.map((permission) => <div key={permission.key} className="py-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{permission.title}</p><p className="text-xs text-gray-500">레벨 기본값: {levelForAccount[permission.key] ? '허용' : '차단'} / 실제 적용: <strong className={effective[permission.key] ? 'text-emerald-700' : 'text-red-600'}>{effective[permission.key] ? '허용' : '차단'}</strong></p></div>
          <Choice value={overrides[permission.key] === null ? 'inherit' : overrides[permission.key]} onChange={(v) => setOverrides((p) => ({ ...p, [permission.key]: v }))} options={ACCOUNT_OPTIONS} disabled={busy}/>
        </div>)}</div>
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap"><button type="button" onClick={() => setOverrides(Object.fromEntries(PERMISSIONS.map(({ key }) => [key, null])))} className="text-sm text-gray-500 underline" disabled={busy}>모두 레벨 기본값으로 초기화</button><button type="button" className={action} onClick={saveAccount} disabled={busy || !accountDirty}>개별 권한 저장</button></div>
      </div>}
    </section>}
    <Feedback message={message} error={error}/>
    <p className="text-xs text-gray-400">모든 저장 작업은 실행한 LV0 계정, 변경 전/후 권한과 함께 DB 감사 기록으로 남습니다. 권한이 철회된 관리자는 새로운 API 요청부터 접근이 거부됩니다.</p>
  </div>;
}
